import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check, X, Search } from "lucide-react";
import { usePortalAnchorRect } from "../lib/usePortalAnchorRect";

/**
 * MultiSelectDropdown
 * - Trigger button shows a compact summary (count + first label)
 * - Popover with optional search field, scrollable checkbox list, Select-All/Clear
 * - Click-outside closes
 *
 * Props:
 *   label:     display label inside the trigger (used when nothing is selected)
 *   options:   [{ value: string, label: string, sub?: string }]
 *   selected:  string[]  — the currently-checked values
 *   onChange:  (next: string[]) => void
 *   searchable?: boolean (default = options.length > 5)
 *   showSelectAll?: boolean (default = true)
 *   testId?:   string for the trigger button (children prefixed with this)
 *   triggerClassName?: extra classes for the trigger button
 *   placement?: 'left' | 'right'  — popover alignment
 *   emptyHint?: string when no options match
 */
export function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  searchable,
  showSelectAll = true,
  testId,
  triggerClassName = "",
  placement = "left",
  emptyHint = "No matches",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Snapshot of the selected values at the moment the popover opened. Used to
  // pin those options to the top of the list without them jumping around when
  // the user clicks further options within the same session. Refreshes on the
  // next popover open.
  const [pinnedSelected, setPinnedSelected] = useState(() => new Set());
  const ref = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const rect = usePortalAnchorRect(open, ref);

  const sel = Array.isArray(selected) ? selected : [];
  const isSearchable = searchable ?? options.length > 5;

  useEffect(() => {
    if (!open) return;
    setPinnedSelected(new Set(sel));
    // Outside-click needs both refs: anchor stays in the original DOM
    // subtree, panel lives in a document.body portal.
    const onDocClick = (e) => {
      const insideAnchor = ref.current && ref.current.contains(e.target);
      const insidePanel = panelRef.current && panelRef.current.contains(e.target);
      if (!insideAnchor && !insidePanel) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    if (isSearchable) setTimeout(() => inputRef.current?.focus(), 30);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
    // `sel` is intentionally NOT a dep — we snapshot the initial
    // selection on popover open only, so freshly checked options stay
    // in-place instead of jumping to the top mid-interaction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isSearchable]);

  const filtered = options.filter((o) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return o.label.toLowerCase().includes(q) || (o.sub || "").toLowerCase().includes(q);
  });

  // Stable ordering: options selected at the moment the popover opened float
  // to the top (preserving their original relative order); everything else
  // keeps its natural order. Uses a stable sort.
  const ordered = filtered
    .map((o, idx) => ({ o, idx, pin: pinnedSelected.has(o.value) ? 0 : 1 }))
    .sort((a, b) => (a.pin - b.pin) || (a.idx - b.idx))
    .map((x) => x.o);

  const toggle = (val) => {
    onChange(sel.includes(val) ? sel.filter((x) => x !== val) : [...sel, val]);
  };

  const summary = (() => {
    if (sel.length === 0) return label;
    if (sel.length === options.length) return `All ${label.toLowerCase()}`;
    if (sel.length === 1) {
      const m = options.find((o) => o.value === sel[0]);
      return m ? m.label : `1 selected`;
    }
    return `${sel.length} selected`;
  })();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={testId}
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen((v) => !v); }}
        className={`inline-flex items-center justify-between gap-2 ${triggerClassName} bg-[#0F0F12] border border-[#27272A] hover:border-zinc-500 text-zinc-200 text-xs uppercase tracking-widest px-3 py-2 transition-colors duration-150 ${open ? "border-[#FF3B30]" : ""} ${disabled ? "opacity-50 cursor-not-allowed hover:border-[#27272A]" : ""}`}
      >
        <span className="truncate text-left">{summary}</span>
        <span className="flex items-center gap-1.5">
          {sel.length > 0 && sel.length < options.length && (
            <span className="text-[10px] text-[#FF3B30] font-bold tabular-nums">{sel.length}</span>
          )}
          <ChevronDown className={`size-3 transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && rect && createPortal(
        <div
          ref={panelRef}
          data-testid={testId ? `${testId}-popover` : undefined}
          className="fixed z-[1200] min-w-[240px] max-w-[340px] border border-[#27272A] bg-[#0A0A0A] shadow-2xl"
          style={{
            // Panel height clamps at ~360px (search + list + footer).
            // Flip above when the popover would exceed the viewport.
            top: rect.bottom + 4 + 360 > window.innerHeight
              ? Math.max(8, rect.top - 360 - 4)
              : rect.bottom + 4,
            // Right-align vs. left-align based on the `placement` prop.
            left: placement === "right"
              ? Math.max(8, Math.min(rect.right - 240, window.innerWidth - 240 - 8))
              : Math.max(8, Math.min(rect.left, window.innerWidth - 240 - 8)),
            minWidth: Math.max(240, rect.width),
          }}
        >
          {isSearchable && (
            <div className="flex items-center gap-2 px-2 border-b border-[#27272A]">
              <Search className="size-3 text-zinc-500 shrink-0" />
              <input
                ref={inputRef}
                data-testid={testId ? `${testId}-search` : undefined}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="w-full bg-transparent text-xs text-white placeholder-zinc-600 py-2 outline-none"
              />
              {query && (
                <button onClick={() => setQuery("")} className="text-zinc-500 hover:text-white">
                  <X className="size-3" />
                </button>
              )}
            </div>
          )}

          {showSelectAll && options.length > 1 && (
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-[#27272A] text-[10px] uppercase tracking-widest">
              <button
                data-testid={testId ? `${testId}-select-all` : undefined}
                onClick={() => onChange(options.map((o) => o.value))}
                className="text-[#0A84FF] hover:underline"
              >
                Select all
              </button>
              <span className="text-zinc-700">·</span>
              <button
                data-testid={testId ? `${testId}-clear` : undefined}
                onClick={() => onChange([])}
                className="text-zinc-500 hover:text-[#FF3B30]"
              >
                Clear
              </button>
            </div>
          )}

          <div className="max-h-64 overflow-y-auto py-1">
            {ordered.length === 0 && (
              <div className="px-3 py-3 text-xs text-zinc-500">{emptyHint}</div>
            )}
            {ordered.map((o, idx) => {
              const on = sel.includes(o.value);
              const isPinned = pinnedSelected.has(o.value);
              const nextIsPinned = idx + 1 < ordered.length && pinnedSelected.has(ordered[idx + 1].value);
              const showDivider = isPinned && !nextIsPinned;
              return (
                <button
                  key={o.value}
                  type="button"
                  data-testid={testId ? `${testId}-option-${o.value}` : undefined}
                  onClick={() => toggle(o.value)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors duration-100 ${
                    on ? "bg-[#FF3B30]/10 text-white" : "text-zinc-300 hover:bg-[#27272A]/60"
                  } ${showDivider ? "border-b border-[#27272A] mb-1 pb-2" : ""}`}
                >
                  <span
                    className={`inline-flex size-3.5 items-center justify-center border shrink-0 ${
                      on ? "bg-[#FF3B30] border-[#FF3B30]" : "border-zinc-600"
                    }`}
                  >
                    {on && <Check className="size-3 text-white" />}
                  </span>
                  <span className="flex-1 truncate">{o.label}</span>
                  {o.sub && <span className="text-[10px] font-mono text-zinc-500 shrink-0">{o.sub}</span>}
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default MultiSelectDropdown;
